// src/components/SecurityScreen.jsx
// Rota: /security
// Endpoint:
// PUT /psy/user/password
//
// O usuário é identificado pelo token JWT.
// Não envia ID pela URL nem pelo body.

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  LockKeyhole,
  LoaderCircle,
  Menu,
  Save,
  ShieldCheck,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/security.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const PASSWORD_ENDPOINT = `${API_BASE}/psy/user/password`;

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_NAME: "psy_usuario",
};

function readStorage(keys = []) {
  for (const key of keys) {
    const sessionValue = sessionStorage.getItem(key);

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }

    const localValue = localStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }
  }

  return "";
}

function writeToken(token) {
  const normalizedToken = String(token || "").trim();

  if (!normalizedToken) {
    return;
  }

  try {
    sessionStorage.setItem(AUTH_KEYS.TOKEN, normalizedToken);
    sessionStorage.setItem("token", normalizedToken);
    localStorage.setItem(AUTH_KEYS.TOKEN, normalizedToken);
  } catch (error) {
    console.warn("Não foi possível atualizar o token no storage:", error);
  }
}

function clearAuthentication() {
  try {
    sessionStorage.removeItem(AUTH_KEYS.TOKEN);
    sessionStorage.removeItem("token");

    localStorage.removeItem(AUTH_KEYS.TOKEN);
    localStorage.removeItem("token");
  } catch (error) {
    console.warn("Não foi possível limpar a autenticação:", error);
  }
}

function getFirstName(name = "") {
  const normalizedName = String(name || "").trim();

  if (!normalizedName) {
    return "";
  }

  return normalizedName.split(/\s+/)[0];
}

function passwordStrength(password = "") {
  const value = String(password || "");

  if (!value) {
    return {
      score: 0,
      label: "Aguardando senha",
      tone: "empty",
      width: "0%",
    };
  }

  let score = 0;

  if (value.length >= 8) score += 1;
  if (value.length >= 10) score += 1;
  if (/[a-z]/.test(value)) score += 1;
  if (/[A-Z]/.test(value)) score += 1;
  if (/[0-9]/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;

  if (score <= 2) {
    return {
      score,
      label: "Senha fraca",
      tone: "weak",
      width: "34%",
    };
  }

  if (score <= 4) {
    return {
      score,
      label: "Senha média",
      tone: "medium",
      width: "68%",
    };
  }

  return {
    score,
    label: "Senha forte",
    tone: "strong",
    width: "100%",
  };
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();

      return (
        payload?.message ||
        payload?.detail ||
        payload?.error ||
        `Erro HTTP ${response.status}.`
      );
    }

    const text = await response.text();

    return text || `Erro HTTP ${response.status}.`;
  } catch {
    return `Erro HTTP ${response.status}.`;
  }
}

function PasswordField({
  label,
  value,
  onChange,
  error,
  visible,
  onToggleVisible,
  disabled = false,
  autoComplete = "current-password",
}) {
  return (
    <label className="sec-field">
      <span className="sec-field__lbl">{label}</span>

      <div
        className={`sec-inputwrap ${
          error ? "is-invalid" : ""
        }`}
      >
        <span className="sec-ico" aria-hidden="true">
          <LockKeyhole size={17} strokeWidth={2} />
        </span>

        <input
          className="sec-input"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          type={visible ? "text" : "password"}
          disabled={disabled}
          autoComplete={autoComplete}
          placeholder="Digite sua senha"
          aria-invalid={Boolean(error)}
        />

        <button
          className="sec-eye"
          type="button"
          onClick={onToggleVisible}
          disabled={disabled}
          aria-label={visible ? "Ocultar senha" : "Visualizar senha"}
          title={visible ? "Ocultar senha" : "Visualizar senha"}
        >
          {visible ? (
            <EyeOff size={19} strokeWidth={2} aria-hidden="true" />
          ) : (
            <Eye size={19} strokeWidth={2} aria-hidden="true" />
          )}
        </button>
      </div>

      {error ? (
        <span className="sec-field__err">{error}</span>
      ) : null}
    </label>
  );
}

export default function SecurityScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState({
    type: "",
    message: "",
  });

  const nomeUsuario = useMemo(
    () =>
      readStorage([
        AUTH_KEYS.USER_NAME,
        "nomeUsuario",
        "usuario",
      ]) || "Usuário",
    []
  );

  const firstName = useMemo(
    () => getFirstName(nomeUsuario),
    [nomeUsuario]
  );

  const strength = useMemo(
    () => passwordStrength(newPassword),
    [newPassword]
  );

  const passwordRules = useMemo(
    () => ({
      minimumLength: newPassword.length >= 8,
      idealLength: newPassword.length >= 10,
      lowercase: /[a-z]/.test(newPassword),
      uppercase: /[A-Z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      symbol: /[^A-Za-z0-9]/.test(newPassword),
    }),
    [newPassword]
  );

  function clearFeedback() {
    setFeedback({
      type: "",
      message: "",
    });
  }

  function validate() {
    const nextErrors = {};

    const current = String(currentPassword || "");
    const next = String(newPassword || "");
    const confirm = String(confirmNewPassword || "");

    if (!current) {
      nextErrors.currentPassword = "Informe sua senha atual.";
    } else if (current.length < 6) {
      nextErrors.currentPassword =
        "A senha atual deve ter pelo menos 6 caracteres.";
    }

    if (!next) {
      nextErrors.newPassword = "Informe a nova senha.";
    } else if (next.length < 8) {
      nextErrors.newPassword =
        "A nova senha deve ter pelo menos 8 caracteres.";
    } else if (!/[A-Z]/.test(next)) {
      nextErrors.newPassword =
        "A nova senha deve possuir pelo menos uma letra maiúscula.";
    } else if (!/[0-9]/.test(next)) {
      nextErrors.newPassword =
        "A nova senha deve possuir pelo menos um número.";
    } else if (!/[^A-Za-z0-9]/.test(next)) {
      nextErrors.newPassword =
        "A nova senha deve possuir pelo menos um símbolo.";
    }

    if (!confirm) {
      nextErrors.confirmNewPassword =
        "Confirme a nova senha.";
    } else if (next !== confirm) {
      nextErrors.confirmNewPassword =
        "As senhas não coincidem.";
    }

    if (current && next && current === next) {
      nextErrors.newPassword =
        "A nova senha precisa ser diferente da senha atual.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }

  function clearForm() {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");

    setErrors({});

    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
  }

  function handleUnauthorized() {
    clearAuthentication();

    navigate("/login", {
      replace: true,
      state: {
        message:
          "Sua sessão expirou. Faça login novamente.",
      },
    });
  }

  async function changePassword() {
    clearFeedback();

    if (!validate()) {
      setFeedback({
        type: "error",
        message:
          "Revise os campos destacados antes de alterar a senha.",
      });

      return;
    }

    const token = readStorage([
      AUTH_KEYS.TOKEN,
      "token",
      "accessToken",
    ]);

    if (!token) {
      setFeedback({
        type: "error",
        message:
          "Token de autenticação não encontrado. Faça login novamente.",
      });

      return;
    }

    setSaving(true);

    try {
      const response = await fetch(PASSWORD_ENDPOINT, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          confirmNewPassword,
        }),
      });

      const newToken = response.headers.get("x-new-token");

      if (newToken) {
        writeToken(newToken);
      }

      if (response.status === 401 || response.status === 403) {
        const message = await readApiError(response);

        if (
          message
            .toLowerCase()
            .includes("senha atual")
        ) {
          setFeedback({
            type: "error",
            message,
          });

          return;
        }

        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        const message = await readApiError(response);

        setFeedback({
          type: "error",
          message:
            message ||
            `Erro ao alterar senha (${response.status}).`,
        });

        return;
      }

      const payload = await response
        .json()
        .catch(() => null);

      setFeedback({
        type: "success",
        message:
          payload?.message ||
          "Senha alterada com sucesso.",
      });

      clearForm();
    } catch (error) {
      console.error("Erro ao alterar senha:", error);

      setFeedback({
        type: "error",
        message:
          "Não foi possível conectar ao servidor. Tente novamente.",
      });
    } finally {
      setSaving(false);
    }
  }

  function goBack() {
    navigate("/settings");
  }

  return (
    <div className="sec-page">
      <div className="sec-bg" aria-hidden="true">
        <span className="sec-bg__orb sec-bg__orb--one" />
        <span className="sec-bg__orb sec-bg__orb--two" />
        <span className="sec-bg__orb sec-bg__orb--three" />
      </div>

      <AppSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <header className="sec-topbar">
        <button
          className="sec-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
          title="Abrir menu"
        >
          <Menu size={21} strokeWidth={2.2} />
        </button>

        <div className="sec-brand">
          <img
            src={Logo}
            alt="PsyAssist Pro"
            className="sec-brand__logo"
          />

          <div className="sec-brand__text">
            <strong>Segurança</strong>
            <span>Alterar senha • {nomeUsuario}</span>
          </div>
        </div>

        <div className="sec-top-actions">
          <button
            className="sec-btn sec-btn--ghost sec-btn--desktop"
            type="button"
            onClick={goBack}
            disabled={saving}
          >
            <ArrowLeft size={16} />
            Configurações
          </button>

          <button
            className="sec-btn sec-btn--primary"
            type="button"
            onClick={changePassword}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle size={16} className="sec-spin" />
                Alterando...
              </>
            ) : (
              <>
                <Save size={16} />
                Alterar
              </>
            )}
          </button>
        </div>
      </header>

      <main className="sec-main">
        <section className="sec-hero">
          <div>
            <span className="sec-kicker">
              <ShieldCheck size={14} />
              Proteção da conta
            </span>

            <h1>Alterar senha</h1>

            <p>
              {firstName ? `${firstName}, ` : ""}
              utilize uma senha forte e diferente das senhas
              usadas em outros sistemas.
            </p>
          </div>

          <aside
            className={`sec-hero-card sec-hero-card--${strength.tone}`}
          >
            <div
              className="sec-hero-card__icon"
              aria-hidden="true"
            >
              <ShieldCheck size={24} strokeWidth={2} />
            </div>

            <div>
              <small>Nível da nova senha</small>
              <strong>{strength.label}</strong>

              <span>
                Recomendado: 8 ou mais caracteres, letra
                maiúscula, número e símbolo.
              </span>
            </div>
          </aside>
        </section>

        <section className="sec-panel">
          <div className="sec-panel__head">
            <div>
              <span>Senha</span>
              <h2>Credenciais de acesso</h2>

              <p>
                Confirme sua senha atual e escolha uma nova
                senha de acesso.
              </p>
            </div>

            <div className="sec-mode">Conta</div>
          </div>

          {feedback.message ? (
            <div
              className={`sec-feedback sec-feedback--${
                feedback.type || "info"
              }`}
              role={
                feedback.type === "error"
                  ? "alert"
                  : "status"
              }
            >
              {feedback.message}
            </div>
          ) : null}

          <div className="sec-form">
            <PasswordField
              label="Senha atual"
              value={currentPassword}
              onChange={(value) => {
                setCurrentPassword(value);
                clearFeedback();

                if (errors.currentPassword) {
                  setErrors((previous) => ({
                    ...previous,
                    currentPassword: "",
                  }));
                }
              }}
              error={errors.currentPassword}
              visible={showCurrent}
              onToggleVisible={() =>
                setShowCurrent((value) => !value)
              }
              disabled={saving}
              autoComplete="current-password"
            />

            <PasswordField
              label="Nova senha"
              value={newPassword}
              onChange={(value) => {
                setNewPassword(value);
                clearFeedback();

                if (errors.newPassword) {
                  setErrors((previous) => ({
                    ...previous,
                    newPassword: "",
                  }));
                }
              }}
              error={errors.newPassword}
              visible={showNew}
              onToggleVisible={() =>
                setShowNew((value) => !value)
              }
              disabled={saving}
              autoComplete="new-password"
            />

            <PasswordField
              label="Confirmar nova senha"
              value={confirmNewPassword}
              onChange={(value) => {
                setConfirmNewPassword(value);
                clearFeedback();

                if (errors.confirmNewPassword) {
                  setErrors((previous) => ({
                    ...previous,
                    confirmNewPassword: "",
                  }));
                }
              }}
              error={errors.confirmNewPassword}
              visible={showConfirm}
              onToggleVisible={() =>
                setShowConfirm((value) => !value)
              }
              disabled={saving}
              autoComplete="new-password"
            />

            <div
              className={`sec-strength sec-strength--${strength.tone}`}
            >
              <div className="sec-strength__top">
                <span>Força da senha</span>
                <strong>{strength.label}</strong>
              </div>

              <div
                className="sec-strength__bar"
                aria-hidden="true"
              >
                <span
                  style={{
                    width: strength.width,
                  }}
                />
              </div>

              <ul className="sec-rules">
                <li
                  className={
                    passwordRules.minimumLength
                      ? "is-ok"
                      : ""
                  }
                >
                  Mínimo de 8 caracteres
                </li>

                <li
                  className={
                    passwordRules.idealLength
                      ? "is-ok"
                      : ""
                  }
                >
                  Ideal com 10 ou mais caracteres
                </li>

                <li
                  className={
                    passwordRules.lowercase
                      ? "is-ok"
                      : ""
                  }
                >
                  Letra minúscula
                </li>

                <li
                  className={
                    passwordRules.uppercase
                      ? "is-ok"
                      : ""
                  }
                >
                  Letra maiúscula
                </li>

                <li
                  className={
                    passwordRules.number
                      ? "is-ok"
                      : ""
                  }
                >
                  Número
                </li>

                <li
                  className={
                    passwordRules.symbol
                      ? "is-ok"
                      : ""
                  }
                >
                  Símbolo
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="sec-actions">
          <button
            className="sec-btn sec-btn--ghost-dark"
            type="button"
            onClick={goBack}
            disabled={saving}
          >
            <ArrowLeft size={16} />
            Cancelar
          </button>

          <button
            className="sec-btn sec-btn--primary"
            type="button"
            onClick={changePassword}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle size={16} className="sec-spin" />
                Alterando...
              </>
            ) : (
              <>
                <Save size={16} />
                Alterar senha
              </>
            )}
          </button>
        </section>

        <div className="sec-hint">
          Após alterar a senha, guarde-a em local seguro e
          evite reutilizar senhas antigas.
        </div>
      </main>
    </div>
  );
}